import { t } from 'i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { FLOW_TEXT, Font, fonts } from '@/data';

import {
  baseStyles,
  buttonStyles,
  formStyles,
  stepContainerStyles,
} from '@/styles';
import { PersonalisationFlow } from '@/types';
import { resetUploadFile } from '../utils';

import './fg-personalisation-file';

@customElement('fg-personalisation-text-step')
export class FgPersonalisationTextStep extends LitElement {
  @property({ type: Array })
  commonFonts: Font[] = fonts;

  @property({ type: Number })
  maxTextLength = 255;

  @property({ type: Array })
  selectedFlow?: PersonalisationFlow;

  @property({ type: String })
  fileName?: string | null;

  @state()
  private currentFont = '';

  @state()
  private textareaValue = '';

  @state()
  private errorMessage = '';

  private handleFontChange(event: Event) {
    const select = event.currentTarget as HTMLSelectElement;
    this.currentFont = select.value;
  }

  private validateChangeText(event: Event) {
    const textarea = event.currentTarget as HTMLTextAreaElement;
    if (textarea.value.length > this.maxTextLength) {
      this.textareaValue = textarea.value.slice(0, this.maxTextLength);
      return;
    }
    this.textareaValue = textarea.value;
  }

  private handleErrorAnnouncement() {
    this.dispatchEvent(
      new CustomEvent('handle-announcement', {
        composed: true,
        detail: {
          announcement: this.errorMessage,
        },
      }),
    );
  }

  private handleSubmit(event: SubmitEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement,
      formData = new FormData(form),
      fontValue = formData.get('font')?.toString(),
      textValue = formData.get('print-text')?.toString();

    const hasErrorMessage = this.handleErrorMessage(
      textValue || '',
      fontValue || '',
    );

    if (hasErrorMessage) {
      this.handleErrorAnnouncement();
    }

    if (fontValue && textValue) {
      this.sendTextValues(textValue, fontValue);
    }
  }

  private handleErrorMessage(textValue: string, fontValue: string) {
    let checkFontValue = false;
    if (!textValue) {
      this.errorMessage = t('errors.missingText');
      return true;
    }
    if (!fontValue) {
      this.errorMessage = t('errors.missingFont');
      return true;
    }
    if (textValue.length > this.maxTextLength) {
      this.errorMessage = t('errors.textTooLong');
      return true;
    }
    this.commonFonts.filter(font => {
      if (font.fontFamily === fontValue) {
        checkFontValue = true;
      }
    });
    if (!checkFontValue) {
      this.errorMessage = t('errors.wrongValue');
      return true;
    }
    return false;
  }

  sendTextValues(textValue: FormDataEntryValue, fontValue: FormDataEntryValue) {
    this.dispatchEvent(
      new CustomEvent('personalisation-text-step', {
        composed: true,
        detail: {
          textPersonalization: {
            text: textValue,
            font: fontValue,
          },
        },
      }),
    );
  }

  render() {
    return html`
      <div class="step-container">
        ${this.fileName &&
        html`
          <fg-personalisation-file
            .fileName=${this.fileName}
            @personalisation-reset-upload=${(event: CustomEvent) =>
              resetUploadFile(this, event)}
          ></fg-personalisation-file>
        `}
        <form
          class="text-selection"
          @submit=${(event: SubmitEvent) => {
            this.handleSubmit(event);
          }}
        >
          <label for="print-text">
            ${t('content.personalisation.textStep.textToPrint')}
          </label>
          <textarea
            id="print-text"
            name="print-text"
            .placeholder=${t('content.personalisation.textStep.yourText') || ''}
            maxlength=${this.maxTextLength}
            .value=${live(this.textareaValue)}
            @input=${(event: Event) => {
              this.validateChangeText(event);
            }}
          ></textarea>
          <label for="font">
            ${t('content.personalisation.textStep.font')}
          </label>
          <select
            id="font"
            name="font"
            style="font-family: ${this.currentFont}"
            class=${this.currentFont ? 'selected' : 'default'}
            @change=${(event: Event) => {
              this.handleFontChange(event);
            }}
          >
            <option value="" selected disabled hidden>
              ${t('content.personalisation.textStep.chooseFont')}
            </option>
            ${this.commonFonts.map(font => {
              return html`
                <option
                  style="font-family: ${font.fontFamily}"
                  .value=${live(font.name)}
                >
                  ${font.name}
                </option>
              `;
            })}
          </select>
          ${this.errorMessage
            ? html`
                <span class="error-message">${this.errorMessage}</span>
              `
            : nothing}
          <button class="button button-primary" type="submit">
            ${this.selectedFlow === FLOW_TEXT
              ? t('content.personalisation.textStep.stepChooseColors')
              : t('content.personalisation.textStep.stepComplete')}
          </button>
        </form>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    stepContainerStyles,
    formStyles,
    css`
      fg-personalisation-file {
        margin-bottom: 30px;
      }
      .text-selection {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
      label {
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.375rem;
      }
      select {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }
      select.default {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
      }
      .button {
        margin: 20px auto 0;
      }
      .error-message {
        width: 100%;
        text-align: center;
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        margin-top: 20px;
      }
    `,
  ];
}
